import { graphql, useFragment } from 'react-relay';

import {
    type pricingGuidanceHelpers_item$key,
    type pricingGuidanceHelpers_item$data,
} from './__generated__/pricingGuidanceHelpers_item.graphql';

const itemFragment = graphql`
    fragment pricingGuidanceHelpers_item on Item {
        pricingGuidance(
            isTrade: $isTrade
            buyerId: $userId
            userCountryCode: $userCountryCode
            priceBookName: $priceBookName
        ) {
            price {
                convertedAmountList {
                    amount
                    currency
                }
            }
            medianAmount {
                convertedAmountList {
                    amount
                    currency
                }
            }
        }
    }
`;

const HALF_PRICE_RANGE_PART_OF_MEDIAN = 0.2;

export type EstimatedValueRange = { rangeMin: number; rangeMax: number };

export const getEstimatedValueRange = ({
    medianPrice,
}: {
    medianPrice: number;
}): EstimatedValueRange => {
    const halfPriceRange = medianPrice * HALF_PRICE_RANGE_PART_OF_MEDIAN;

    return {
        rangeMin: Math.round(medianPrice - halfPriceRange),
        rangeMax: Math.round(medianPrice + halfPriceRange),
    };
};

const getPriceAmountByCurrency = ({
    price,
    currency,
}: {
    price?: NonNullable<pricingGuidanceHelpers_item$data['pricingGuidance']>['price'];
    currency: string;
}): number => {
    const priceAmount = (price?.convertedAmountList || []).find(
        convertedAmount => convertedAmount?.currency === currency
    );

    return Math.floor(priceAmount?.amount || 0);
};

export const useGetPriceAmountByType = ({
    item: itemRef,
    currency,
    type,
}: {
    item: pricingGuidanceHelpers_item$key | null;
    currency: string;
    type: 'ITEM_PRICE' | 'MEDIAN';
}): number => {
    const item = useFragment(itemFragment, itemRef);
    const { pricingGuidance } = item || {};
    const { price: itemPrice, medianAmount } = pricingGuidance || {};
    let price;

    switch (type) {
        case 'ITEM_PRICE':
            price = itemPrice;
            break;
        case 'MEDIAN':
            price = medianAmount;
            break;
    }

    return getPriceAmountByCurrency({ price, currency });
};
